import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import axios from "axios";
import { API_URL, IMG_URL } from "../../ApiUrl";
import Swal from "sweetalert2";
import Error404 from "../pages/error-404";
import { isValidVenueCategorySlug } from "../../constants/categories";

interface Venues {
  name: string;
  address: string;
  city: string;
  state: string;
  zipcode: number;
  amenities: string[];
  facilities: string[];
  category: string;
  categories?: string[];
  _id: string;
  images: any;
  src: string;
  near_by_location: string;
  vendor_type: any;
  google_location: any;
  description: any;
  gameType?: string;
  sports_details?: { sport?: string; size?: string; price_per_hr?: number }[];
  price_per_hr?: number;
  is_featured_paid?: boolean;
  data?: any;
}

const venueHourlyRate = (venue: Venues): number | null => {
  const sport = String(venue.vendor_type || venue.gameType || venue.category || "").replace(/[_-]/g, " ").trim().toLowerCase();
  const sportRate = (venue.sports_details || []).find((item) =>
    String(item.sport || "").replace(/[_-]/g, " ").trim().toLowerCase() === sport && Number(item.price_per_hr) > 0
  )?.price_per_hr;
  const rate = Number(sportRate ?? venue.price_per_hr);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
};

interface DropdownOption {
  value: string;
  label: string;
  shortLabel?: string;
}

interface VenueSearchNavigationState {
  selectedLocationSort?: { name?: string };
}

const getVenueImage = (images: any): string => {
  if (!images || !Array.isArray(images) || images.length === 0) return "/assets/img/venues/venue-01.jpg";
  const first = images[0];
  const imgStr = typeof first === "string" ? first : (first?.src || first?.url || "");
  if (!imgStr) return "/assets/img/venues/venue-01.jpg";
  if (imgStr.startsWith("http://") || imgStr.startsWith("https://")) return imgStr;
  return `${IMG_URL}${imgStr}`;
};

const DATE_OPTIONS: DropdownOption[] = [
  { value: "", label: "Select Date" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this-weekend", label: "This Weekend" },
  { value: "next-7-days", label: "Next 7 Days" },
];

// Custom Modern Theme Dropdown Component (replaces native OS select)
const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  icon,
  searchable = true,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  icon: string;
  searchable?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setTimeout(() => {
        if (searchInputRef.current) searchInputRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleToggleOpen = () => {
    if (!isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 280 && rect.top > 200) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
    setIsOpen(!isOpen);
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const query = searchTerm.toLowerCase().trim();
    return options.filter((opt) => opt.label.toLowerCase().startsWith(query));
  }, [options, searchTerm]);

  return (
    <div className="position-relative w-100" ref={dropdownRef} style={{ zIndex: isOpen ? 9998 : 1 }}>
      <button
        type="button"
        className="btn w-100 d-flex align-items-center justify-content-between px-3 bg-white shadow-sm"
        onClick={handleToggleOpen}
        style={{
          height: "42px",
          fontSize: "12px",
          fontWeight: "500",
          color: selectedOption && selectedOption.value !== "" ? "#1E293B" : "#64748B",
          borderRadius: "12px",
          border: isOpen ? "1.5px solid #22C55E" : "1px solid #E2E8F0",
          boxShadow: isOpen ? "0 0 0 3px rgba(34, 197, 94, 0.15)" : "none",
          transition: "all 0.2s ease"
        }}
      >
        <span className="d-flex align-items-center text-truncate me-2">
          <i className={`${icon} text-success me-2`} style={{ fontSize: "14px", flexShrink: 0 }} />
          <span className="text-truncate">{selectedOption ? (selectedOption.shortLabel || selectedOption.label) : placeholder}</span>
        </span>
        <i
          className="feather-chevron-down ms-1 flex-shrink-0"
          style={{
            fontSize: "13px",
            color: "#22C55E",
            transform: isOpen ? (openUpward ? "rotate(0deg)" : "rotate(180deg)") : (openUpward ? "rotate(180deg)" : "rotate(0deg)"),
            transition: "transform 0.2s ease"
          }}
        />
      </button>

      {isOpen && (
        <div
          className="position-absolute start-0 bg-white shadow-lg border overflow-hidden"
          style={{
            zIndex: 9999,
            minWidth: "100%",
            width: "100%",
            maxWidth: "min(280px, calc(100vw - 24px))",
            boxSizing: "border-box",
            borderColor: "#E2E8E3",
            borderRadius: "14px",
            boxShadow: openUpward
              ? "0 -10px 25px rgba(0,0,0,0.12)"
              : "0 10px 25px rgba(0,0,0,0.12)",
            ...(openUpward
              ? { bottom: "calc(100% + 6px)" }
              : { top: "calc(100% + 4px)" })
          }}
        >
          {searchable && options.length > 5 && (
            <div className="p-2 border-bottom bg-white" style={{ borderColor: "#F1F5F9" }}>
              <div className="position-relative d-flex align-items-center">
                <i className="feather-search position-absolute text-muted" style={{ fontSize: "12px", left: "12px", pointerEvents: "none", zIndex: 5 }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontSize: "11px",
                    borderRadius: "8px",
                    borderColor: "#CBD5E1",
                    backgroundColor: "#F8FAFC",
                    paddingLeft: "34px",
                    paddingRight: "10px",
                    height: "34px"
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ maxHeight: "min(320px, calc(100vh - 180px))", overflowY: "auto", overscrollBehavior: "contain", padding: "4px 0" }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`btn w-100 text-start px-3 py-2 d-flex align-items-center justify-content-between gap-2 ki-custom-dropdown-option ${isSelected ? "is-selected" : ""}`}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    style={{
                      fontSize: "12px",
                      fontWeight: isSelected ? "700" : "500",
                      color: isSelected ? "#FFFFFF" : "#334155",
                      backgroundColor: isSelected ? "#166534" : "transparent",
                      transition: "background-color 0.15s ease"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = "#F8FAFC";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <span className="ki-custom-dropdown-option-label" style={{ color: isSelected ? "#FFFFFF" : "#334155" }}>{opt.label}</span>
                    {isSelected && <i className="feather-check flex-shrink-0" style={{ fontSize: "12px", color: "#FFFFFF" }} />}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-3 text-center text-muted" style={{ fontSize: "11px" }}>
                No matching results
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Mini Calendar Dropdown Component (with greyed out past dates)
const MiniCalendarDropdown = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (dateStr: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewDate, setViewDate] = useState<Date>(() => new Date());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  // Days calculation
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const handlePrevMonth = () => {
    const prev = new Date(year, month - 1, 1);
    if (prev.getFullYear() < today.getFullYear() || (prev.getFullYear() === today.getFullYear() && prev.getMonth() < today.getMonth())) {
      return;
    }
    setViewDate(prev);
  };

  const handleNextMonth = () => {
    setViewDate(new Date(year, month + 1, 1));
  };

  const isPrevDisabled = year < today.getFullYear() || (year === today.getFullYear() && month <= today.getMonth());

  const formatDateStr = (y: number, m: number, d: number) => {
    const mm = String(m + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  };

  const formatDisplayLabel = (dateStr: string) => {
    if (!dateStr) return "Select Date";
    const [y, m, d] = dateStr.split("-").map(Number);
    const sel = new Date(y, m - 1, d);
    sel.setHours(0, 0, 0, 0);
    if (sel.getTime() === today.getTime()) return "Today";
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (sel.getTime() === tomorrow.getTime()) return "Tomorrow";
    return sel.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="position-relative w-100" ref={dropdownRef} style={{ zIndex: isOpen ? 9998 : 1 }}>
      <button
        type="button"
        className="btn w-100 d-flex align-items-center justify-content-between px-3 bg-white shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          height: "42px",
          fontSize: "12px",
          fontWeight: "500",
          color: value ? "#1E293B" : "#64748B",
          borderRadius: "12px",
          border: isOpen ? "1.5px solid #22C55E" : "1px solid #E2E8F0",
          boxShadow: isOpen ? "0 0 0 3px rgba(34, 197, 94, 0.15)" : "none",
          transition: "all 0.2s ease"
        }}
      >
        <span className="d-flex align-items-center text-truncate me-2">
          <i className="feather-calendar text-success me-2" style={{ fontSize: "14px", flexShrink: 0 }} />
          <span className="text-truncate">{formatDisplayLabel(value)}</span>
        </span>
        <i
          className="feather-chevron-down ms-1 flex-shrink-0"
          style={{
            fontSize: "13px",
            color: "#22C55E",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease"
          }}
        />
      </button>

      {isOpen && (
        <div
          className="position-absolute start-0 bg-white shadow-lg border p-3 mt-1"
          style={{
            zIndex: 9999,
            width: "250px",
            borderColor: "#E2E8E3",
            borderRadius: "16px",
            boxShadow: "0 12px 30px rgba(0,0,0,0.12)"
          }}
        >
          {/* Calendar Header */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <button
              type="button"
              className="btn btn-sm p-1 text-muted"
              onClick={handlePrevMonth}
              disabled={isPrevDisabled}
              style={{ opacity: isPrevDisabled ? 0.3 : 1, cursor: isPrevDisabled ? "not-allowed" : "pointer" }}
            >
              <i className="feather-chevron-left" style={{ fontSize: "13px" }} />
            </button>
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#0F172A" }}>
              {monthNames[month]} {year}
            </span>
            <button
              type="button"
              className="btn btn-sm p-1 text-muted"
              onClick={handleNextMonth}
            >
              <i className="feather-chevron-right" style={{ fontSize: "13px" }} />
            </button>
          </div>

          {/* Days of week */}
          <div className="d-grid mb-1.5" style={{ gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center" }}>
            {daysOfWeek.map((day) => (
              <span key={day} style={{ fontSize: "10px", fontWeight: "700", color: "#94A3B8" }}>
                {day}
              </span>
            ))}
          </div>

          {/* Date Grid */}
          <div className="d-grid gap-1" style={{ gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center" }}>
            {/* Empty slots for start of month */}
            {Array.from({ length: firstDayOfMonth }).map((_, idx) => (
              <div key={`empty-${idx}`} />
            ))}

            {/* Days of month */}
            {Array.from({ length: daysInMonth }).map((_, idx) => {
              const dayNum = idx + 1;
              const dateObj = new Date(year, month, dayNum);
              dateObj.setHours(0, 0, 0, 0);

              const isPast = dateObj.getTime() < today.getTime();
              const dateStr = formatDateStr(year, month, dayNum);
              const isSelected = value === dateStr;
              const isToday = dateObj.getTime() === today.getTime();

              return (
                <button
                  key={dayNum}
                  type="button"
                  disabled={isPast}
                  onClick={() => {
                    onChange(dateStr);
                    setIsOpen(false);
                  }}
                  className="btn btn-sm p-0 d-flex align-items-center justify-content-center mx-auto"
                  style={{
                    width: "27px",
                    height: "27px",
                    borderRadius: "7px",
                    fontSize: "11px",
                    fontWeight: isSelected ? "700" : isToday ? "700" : "500",
                    color: isPast ? "#CBD5E1" : isSelected ? "#FFFFFF" : isToday ? "#22C55E" : "#334155",
                    backgroundColor: isSelected ? "#22C55E" : "transparent",
                    border: isToday && !isSelected ? "1px solid #22C55E" : "none",
                    cursor: isPast ? "not-allowed" : "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (!isPast && !isSelected) e.currentTarget.style.backgroundColor = "#F0FDF4";
                  }}
                  onMouseLeave={(e) => {
                    if (!isPast && !isSelected) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {dayNum}
                </button>
              );
            })}
          </div>

          {/* Quick Actions Footer */}
          <div className="d-flex align-items-center justify-content-between pt-2 mt-2 border-top" style={{ borderColor: "#F1F5F9" }}>
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0"
              style={{ fontSize: "11px", fontWeight: "600", color: "#22C55E" }}
              onClick={() => {
                const todayStr = formatDateStr(today.getFullYear(), today.getMonth(), today.getDate());
                onChange(todayStr);
                setIsOpen(false);
              }}
            >
              Today
            </button>
            {value && (
              <button
                type="button"
                className="btn btn-link text-decoration-none p-0 text-muted"
                style={{ fontSize: "11px", fontWeight: "500" }}
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                }}
              >
                Clear
              </button>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

// Custom Multi-Select Dropdown Component (with checkboxes)
const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  icon,
  forceUpward,
}: {
  options: DropdownOption[];
  selectedValues: string[];
  onChange: (vals: string[]) => void;
  placeholder: string;
  icon: string;
  forceUpward?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (val: string) => {
    if (val === "any" || val === "") {
      onChange([]);
      return;
    }
    if (selectedValues.includes(val)) {
      onChange(selectedValues.filter((v) => v !== val));
    } else {
      onChange([...selectedValues, val]);
    }
  };

  const handleToggleOpen = () => {
    if (!isOpen && dropdownRef.current) {
      if (forceUpward) {
        setOpenUpward(true);
      } else {
        const rect = dropdownRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < 280 && rect.top > 200) {
          setOpenUpward(true);
        } else {
          setOpenUpward(false);
        }
      }
    }
    setIsOpen(!isOpen);
  };

  const formatButtonLabel = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const opt = options.find((o) => o.value === selectedValues[0]);
      return opt ? opt.label : placeholder;
    }
    return `${selectedValues.length} Selected`;
  };

  return (
    <div className="position-relative w-100" ref={dropdownRef} style={{ zIndex: isOpen ? 9998 : 1 }}>
      <button
        type="button"
        className="btn w-100 d-flex align-items-center justify-content-between px-3 bg-white shadow-sm"
        onClick={handleToggleOpen}
        style={{
          height: "42px",
          fontSize: "12px",
          fontWeight: "500",
          color: selectedValues.length > 0 ? "#15803D" : "#64748B",
          borderRadius: "12px",
          border: isOpen ? "1.5px solid #22C55E" : selectedValues.length > 0 ? "1.5px solid #22C55E" : "1px solid #E2E8F0",
          boxShadow: isOpen ? "0 0 0 3px rgba(34, 197, 94, 0.15)" : "none",
          transition: "all 0.2s ease"
        }}
      >
        <span className="d-flex align-items-center text-truncate me-2">
          <i className={`${icon} text-success me-2`} style={{ fontSize: "14px", flexShrink: 0 }} />
          <span className="text-truncate">{formatButtonLabel()}</span>
        </span>
        <i
          className="feather-chevron-down ms-1 flex-shrink-0"
          style={{
            fontSize: "13px",
            color: "#22C55E",
            transform: isOpen ? (openUpward ? "rotate(0deg)" : "rotate(180deg)") : (openUpward ? "rotate(180deg)" : "rotate(0deg)"),
            transition: "transform 0.2s ease"
          }}
        />
      </button>

      {isOpen && (
        <div
          className="position-absolute start-0 bg-white shadow-lg border"
          style={{
            zIndex: 9999,
            minWidth: "100%",
            width: "max-content",
            maxWidth: "260px",
            borderColor: "#E2E8E3",
            borderRadius: "14px",
            overflow: "hidden",
            boxShadow: openUpward
              ? "0 -10px 25px rgba(0,0,0,0.12)"
              : "0 10px 25px rgba(0,0,0,0.12)",
            ...(openUpward
              ? { bottom: "calc(100% + 6px)" }
              : { top: "calc(100% + 4px)" })
          }}
        >
          <div style={{ maxHeight: "220px", overflowY: "auto", padding: "4px 0" }}>
            {options.map((opt) => {
              if (opt.value === "any" || opt.value === "") return null;
              const isChecked = selectedValues.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  className="px-3 py-2 d-flex align-items-center justify-content-between cursor-pointer"
                  onClick={() => handleToggle(opt.value)}
                  style={{
                    fontSize: "12px",
                    fontWeight: isChecked ? "600" : "500",
                    color: isChecked ? "#15803D" : "#334155",
                    backgroundColor: isChecked ? "#F0FDF4" : "transparent",
                    cursor: "pointer",
                    transition: "background-color 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (!isChecked) e.currentTarget.style.backgroundColor = "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    if (!isChecked) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "5px",
                        border: isChecked ? "none" : "1.5px solid #CBD5E1",
                        backgroundColor: isChecked ? "#22C55E" : "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.15s ease"
                      }}
                    >
                      {isChecked && <i className="feather-check text-white" style={{ fontSize: "11px", fontWeight: "bold" }} />}
                    </div>
                    <span>{opt.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top bg-light" style={{ borderColor: "#F1F5F9" }}>
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 text-muted"
              style={{ fontSize: "11px", fontWeight: "500" }}
              onClick={() => onChange([])}
            >
              Clear All
            </button>
            <button
              type="button"
              className="btn btn-success btn-sm py-0.5 px-2.5 rounded-pill"
              style={{ fontSize: "11px", fontWeight: "600", backgroundColor: "#22C55E", borderColor: "#22C55E" }}
              onClick={() => setIsOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const GRASS_OPTIONS: DropdownOption[] = [
  { value: "any", label: "Any Grass" },
  { value: "box", label: "Box Cricket Turf" },
  { value: "natural", label: "Natural Grass Ground" },
  { value: "artificial", label: "Artificial Turf" },
];

const VENUE_SETTING_OPTIONS: DropdownOption[] = [{ value: "", label: "Any setting" }, { value: "open", label: "Open" }, { value: "covered", label: "Covered" }];
const VENUE_LEVEL_OPTIONS: DropdownOption[] = [{ value: "", label: "Any level" }, { value: "ground-floor", label: "Ground Floor" }, { value: "terrace", label: "Terrace" }];
const VENUE_CONDITION_OPTIONS: DropdownOption[] = [{ value: "", label: "Any condition" }, { value: "new", label: "New" }, { value: "old", label: "Old" }];
const YES_NO_OPTIONS: DropdownOption[] = [{ value: "", label: "Any" }, { value: "yes", label: "Yes" }, { value: "no", label: "No" }];
const AC_OPTIONS: DropdownOption[] = [{ value: "", label: "Any" }, { value: "ac", label: "AC" }, { value: "non-ac", label: "Non-AC" }];

// Fallback amenity list (used only when the API returns no amenity data at all).
// Values are slug keys matched against venue amenities/facilities via AMENITY_KEYWORDS.
const FALLBACK_AMENITY_OPTIONS: DropdownOption[] = [
  { value: "parking", label: "Parking" },
  { value: "washrooms", label: "Washrooms" },
  { value: "lockers", label: "Lockers" },
  { value: "food", label: "Food / Canteen" },
  { value: "water", label: "Drinking Water" },
  { value: "floodlights", label: "Floodlights / Lighting" },
  { value: "seating", label: "Seating" },
  { value: "wi-fi", label: "Wi-Fi" },
  { value: "cctv", label: "CCTV / Security" },
  { value: "first-aid", label: "First Aid" },
  { value: "shower", label: "Shower" },
  { value: "sound-system", label: "Sound System" },
];

// Keyword map used to match fallback amenity slugs against real venue data values
const AMENITY_KEYWORDS: Record<string, string[]> = {
  parking: ["parking"],
  washrooms: ["washroom", "toilet"],
  lockers: ["locker"],
  food: ["food", "canteen", "cafe", "snack"],
  water: ["drinking water", "water"],
  floodlights: ["floodlight", "lighting", "light"],
  seating: ["seating", "sit"],
  "wi-fi": ["wi-fi", "wifi", "internet"],
  cctv: ["cctv", "camera", "security"],
  "first-aid": ["first aid", "medical"],
  shower: ["shower"],
  "sound-system": ["sound", "speaker", "music"],
};

const SORT_OPTIONS: DropdownOption[] = [
  { value: "popular", label: "Popular" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "size-small", label: "Size: Small to Large" },
  { value: "size-large", label: "Size: Large to Small" },
  { value: "rating-high", label: "Rating: High to Low" },
  { value: "newest", label: "Newest First" },
  { value: "name-az", label: "Name: A to Z" },
];

export default function VenueByCategory() {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venues[]>([]);
  const [favorites, setFavorites] = useState<Record<string, boolean>>({});

  const thisCategory = useParams<{ type: string }>();
  const routeLocation = useLocation();
  const routeState = routeLocation.state as VenueSearchNavigationState | null;
  const categorySelected = thisCategory?.type || "";

  // Move old shared/bookmarked URLs to the canonical Turf URL while keeping
  // the legacy route readable during the transition.
  useEffect(() => {
    if (categorySelected === "cricket-grounds") {
      navigate("/sports-venue/turf", { replace: true, state: routeState });
    }
  }, [categorySelected, navigate, routeState]);

  const getVenueSize = (venue: Venues) => {
    if (Array.isArray(venue.sports_details) && venue.sports_details.length > 0) {
      const activeSport = (thisCategory?.type || "").replace(/-/g, " ").toLowerCase();
      const match = venue.sports_details.find(
        (sd: { sport?: string; size?: string }) => sd && sd.sport && sd.sport.toLowerCase().includes(activeSport)
      );
      if (match && match.size) {
        return match.size;
      }
      const firstWithSize = venue.sports_details.find((sd: { sport?: string; size?: string }) => sd && sd.size);
      if (firstWithSize) {
        return firstWithSize.size;
      }
    }
    if (venue.data && venue.data.vendor_data && Array.isArray(venue.data.vendor_data)) {
      const dimensionObj = venue.data.vendor_data.find((item: any) => item && item.key === "dimension");
      if (dimensionObj && dimensionObj.value) {
        return `${dimensionObj.value} ft`;
      }
      const sizeObj = venue.data.vendor_data.find((item: any) => item && item.key === "size");
      if (sizeObj && sizeObj.value) {
        return sizeObj.value;
      }
      const areaObj = venue.data.vendor_data.find((item: any) => item && item.key === "total_area_in_sq_feet");
      if (areaObj && areaObj.value) {
        return `${areaObj.value} sq. ft.`;
      }
    }
    if (venue.gameType && venue.gameType.toLowerCase() !== (thisCategory?.type || "").toLowerCase()) {
      return venue.gameType;
    }
    return "Standard";
  };

  const formatVenueDimensions = (size: string) => {
    const dimensions = String(size || "").match(/\d+(?:\.\d+)?/g) || [];
    if (dimensions.length >= 3) return `${dimensions[0]}L × ${dimensions[1]}W × ${dimensions[2]}H`;
    if (dimensions.length === 2) return `${dimensions[0]}L × ${dimensions[1]}W`;
    return size || "Not specified";
  };

  // Filter States
  const [selectedSport, setSelectedSport] = useState<string>(categorySelected ? categorySelected.toLowerCase() : "all");
  const [locationName, setLocationName] = useState<string>(() => routeState?.selectedLocationSort?.name || "");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [selectedGrassType, setSelectedGrassType] = useState<string>("any");
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [venueSetting, setVenueSetting] = useState("");
  const [venueLevel, setVenueLevel] = useState("");
  const [venueCondition, setVenueCondition] = useState("");
  const [soundSystem, setSoundSystem] = useState("");
  const [airConditioning, setAirConditioning] = useState("");
  const [cafeteria, setCafeteria] = useState("");
  const [venueSize, setVenueSize] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState<string>("popular");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Today's date in local YYYY-MM-DD format (prevents selecting past dates)
  const todayStr = useMemo(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  // 30-minute time slot options across 24 hours
  const HALF_HOUR_OPTIONS = useMemo(() => {
    const slots: { value: string; label: string }[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const val = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        const period = h >= 12 ? "PM" : "AM";
        const displayH = h % 12 === 0 ? 12 : h % 12;
        const label = `${String(displayH).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
        slots.push({ value: val, label });
      }
    }
    return slots;
  }, []);

  // Format HH:mm into 12h AM/PM for badges/display
  const formatTime12h = (time24: string): string => {
    if (!time24) return "";
    const [hStr, mStr] = time24.split(":");
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (isNaN(h) || isNaN(m)) return time24;
    const period = h >= 12 ? "PM" : "AM";
    const displayH = h % 12 === 0 ? 12 : h % 12;
    return `${String(displayH).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
  };

  // Helper to calculate +30 minutes automatically
  const addThirtyMinutes = (timeStr: string): string => {
    if (!timeStr) return "";
    const [hStr, mStr] = timeStr.split(":");
    let h = parseInt(hStr, 10);
    let m = parseInt(mStr, 10) + 30;
    if (m >= 60) {
      h = (h + 1) % 24;
      m -= 60;
    }
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  // When From time changes, automatically set To time to +30 minutes
  const handleStartTimeChange = (val: string) => {
    setStartTime(val);
    if (val) {
      const autoEnd = addThirtyMinutes(val);
      setEndTime(autoEnd);
    } else {
      setEndTime("");
    }
  };

  useEffect(() => {
    if (categorySelected) {
      setSelectedSport(categorySelected.toLowerCase());
    }
  }, [categorySelected]);

  useEffect(() => {
    const requestedLocation = routeState?.selectedLocationSort?.name;
    if (requestedLocation) setLocationName(requestedLocation);
  }, [routeState]);

  const categoryTitle = selectedSport === "cricket-grounds"
    ? "Turf"
    : selectedSport && selectedSport !== "all"
    ? selectedSport
        .split("-")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "Sports";
  const categoryListingLabel = categoryTitle === "Turf"
    ? "turf venues"
    : `${categoryTitle.toLowerCase()} grounds`;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${categoryTitle} Venues - Khelo Indore`;
  }, [categoryTitle]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        // Send active filters to the backend (all optional params). The backend
        // filters when deployed; the client re-applies the same rules below so
        // results stay correct either way.
        const params: Record<string, string> = {};
        if (selectedSport && selectedSport !== "all" && selectedSport !== "other-sports") {
          // "cricket-grounds" is the public legacy slug for the Turf
          // category. Provider records and the API use the canonical value
          // "turf", so translate it before requesting listings.
          params.sport = selectedSport === "cricket-grounds" ? "turf" : selectedSport;
        }
        if (locationName) params.location = locationName;
        if (selectedGrassType && selectedGrassType !== "any") params.grassType = selectedGrassType;
        if (selectedAmenities.length > 0) params.amenities = selectedAmenities.join(",");
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (startTime) params.startTime = startTime;
        if (endTime) params.endTime = endTime;
        if (venueSetting) params.venueSetting = venueSetting;
        if (venueLevel) params.venueLevel = venueLevel;
        if (venueCondition) params.venueCondition = venueCondition;
        if (soundSystem) params.soundSystem = soundSystem;
        if (airConditioning) params.airConditioning = airConditioning;
        if (cafeteria) params.cafeteria = cafeteria;
        if (venueSize) params.venueSize = venueSize;
        if (minPrice) params.minPrice = minPrice;
        if (maxPrice) params.maxPrice = maxPrice;
        if (sortBy && sortBy !== "popular") params.sort = sortBy;
        const response = await axios.get(`${API_URL}/web/venue/getVenue`, { params });
        const venuesData = response.data.venue || [];
        const mappedData = venuesData.map((venues: any) => ({
          name: venues.name,
          address: venues.address,
          city: venues.city,
          state: venues.state,
          zipcode: venues.zipcode,
          amenities: Array.isArray(venues.amenities) ? venues.amenities : [],
          facilities: Array.isArray(venues.facilities) ? venues.facilities : [],
          images: venues.images,
          category: venues.category,
          _id: venues._id,
          near_by_location: venues.near_by_location,
          vendor_type: venues.vendor_type,
          price_per_hr: venues.price_per_hr,
          is_featured_paid: Boolean(venues.is_featured_paid),
          google_location: venues.google_location,
          description: venues.description || "",
          gameType: venues.gameType,
          sports_details: Array.isArray(venues.sports_details) ? venues.sports_details : [],
          data: venues.data,
        }));
        setVenues(mappedData);
      } catch {
        // The request failure is handled by the surrounding UI state.
      }
    };
    fetchVenues();
  }, [selectedSport, locationName, selectedGrassType, selectedAmenities, startDate, endDate, startTime, endTime, venueSetting, venueLevel, venueCondition, soundSystem, airConditioning, cafeteria, venueSize, minPrice, maxPrice, sortBy]);

  // Amenity options are built from the real amenities/facilities the API returns
  // (falling back to common amenities only when the data has none).
  const amenityOptions: DropdownOption[] = useMemo(() => {
    const unique = Array.from(
      new Set(
        venues.flatMap((v) =>
          [...(v.amenities || []), ...(v.facilities || [])]
            .map((a) => String(a).trim())
            .filter(Boolean)
        )
      )
    );
    const dynamic = unique.map((a) => ({ value: a, label: a }));
    return [
      { value: "any", label: "Any Amenities" },
      ...(dynamic.length > 0 ? dynamic : FALLBACK_AMENITY_OPTIONS),
    ];
  }, [venues]);

  const toggleFavorite = (venueId: string) => {
    const token = localStorage.getItem("token");
    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Please Login First",
        text: "You need to log in to add venues to your favourites.",
        showCancelButton: true,
        confirmButtonColor: "#22C55E",
        cancelButtonColor: "#d33",
        confirmButtonText: "Login Now",
        cancelButtonText: "Cancel"
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/login");
        }
      });
      return;
    }

    const nextStatus = !favorites[venueId];
    localStorage.setItem(`fav_venue_${venueId}`, String(nextStatus));
    setFavorites((prev) => ({
      ...prev,
      [venueId]: nextStatus,
    }));

    Swal.fire({
      icon: nextStatus ? "success" : "info",
      title: `${nextStatus ? "Saved to Favourites!" : "Removed from Favourites"}`,
      text: `${nextStatus ? "Venue added to your favorites list." : "Venue removed from your favorites list."}`,
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const locationOptions: DropdownOption[] = useMemo(() => {
    const uniqueLocs = [
      ...new Set(
        venues
          .map((v) => (v.near_by_location ? String(v.near_by_location).replace(/_/g, " ").trim() : ""))
          .filter(Boolean)
      ),
    ];
    // Keep the placeholder in the trigger only; it must not be a selectable
    // option that appears as the active location in the mobile list.
    return uniqueLocs.map((loc) => ({ value: loc, label: loc }));
  }, [venues]);

  // Unified Filtering & Sorting Engine
  const displayList = useMemo(() => {
    const result = venues.filter((t: any) => {
      // 0. Name Search Filter
      if (searchQuery) {
        const venueName = (t.name || "").toLowerCase();
        if (!venueName.includes(searchQuery.toLowerCase())) {
          return false;
        }
      }

      // 1. Sports Filter
      if (selectedSport && selectedSport !== "all") {
        const vt = (t.vendor_type || "").toLowerCase().replace(/_/g, " ").trim();
        const cat = (t.category || "").toLowerCase().replace(/_/g, " ").trim();
        const selectedCategories = Array.isArray(t.categories)
          ? t.categories.map((category: string) => String(category).toLowerCase().replace(/[_-]/g, " ").trim())
          : [];

        if (selectedSport === "other-sports") {
          const matchesCategory = (keyword: string) => selectedCategories.some((category) => category.includes(keyword)) || vt.includes(keyword) || cat.includes(keyword);
          const isCricket = matchesCategory("cricket") || matchesCategory("turf");
          const isBadminton = matchesCategory("badminton");
          const isSwimming = matchesCategory("swim");
          const isFootball = matchesCategory("football");
          const isPickleball = matchesCategory("pickle");
          const isTennis = matchesCategory("tennis") && !matchesCategory("table tennis");
          const isBasketball = matchesCategory("basketball");
          const isTableTennis = matchesCategory("table tennis");

          if (isCricket || isBadminton || isSwimming || isFootball || isPickleball || isTennis || isBasketball || isTableTennis) {
            return false;
          }
        } else {
          const targetCat = selectedSport.replace(/-/g, " ").trim();
          const normalizedTarget = targetCat.replace(/&/g, "and");
          const isTurfCategory = selectedSport === "cricket-grounds" || selectedSport === "turf";
          const matches = [...selectedCategories, vt, cat]
            .filter(Boolean)
            .map((field) => field.replace(/&/g, "and").replace(/-/g, " "))
            .some((field) => isTurfCategory
              ? field.includes("cricket") || field.includes("turf")
              : field.includes(normalizedTarget));
          if (!matches) return false;
        }
      }

      // 2. Location Filter
      if (locationName) {
        const formattedLocation = (t.near_by_location || "").replace(/_/g, " ").toLowerCase();
        const targetLoc = locationName.toLowerCase();
        if (!formattedLocation || !formattedLocation.includes(targetLoc)) {
          return false;
        }
      }

      // 3. Grass Type Filter (derived from the venue's vendor_type / category / name)
      if (selectedGrassType && selectedGrassType !== "any") {
        const grassText = `${t.vendor_type || ""} ${t.category || ""} ${t.name || ""}`
          .toLowerCase()
          .replace(/_/g, " ");
        const matchesGrass =
          selectedGrassType === "box"
            ? grassText.includes("box")
            : selectedGrassType === "natural"
            ? grassText.includes("ground") || grassText.includes("natural")
            : selectedGrassType === "artificial"
            ? grassText.includes("turf") || grassText.includes("astro") || grassText.includes("artificial")
            : true;
        if (!matchesGrass) return false;
      }

      // 4. Amenities Multi-Select Filter (matches real amenities + facilities fields)
      if (selectedAmenities.length > 0) {
        const venueAmenities = [...(t.amenities || []), ...(t.facilities || [])]
          .map((a) => String(a).toLowerCase());
        const hasAllAmenities = selectedAmenities.every((amenity) => {
          const keywords = AMENITY_KEYWORDS[amenity] || [amenity.replace(/-/g, " ").toLowerCase()];
          return keywords.some((kw) => venueAmenities.some((a) => a.includes(kw)));
        });
        if (!hasAllAmenities) return false;
      }

      return true;
    });

    // Sorting logic
    if (sortBy === "price-low") {
      result.sort((a, b) => Number(a.price_per_hr || 0) - Number(b.price_per_hr || 0));
    } else if (sortBy === "price-high") {
      result.sort((a, b) => Number(b.price_per_hr || 0) - Number(a.price_per_hr || 0));
    }

    return result;
  }, [venues, selectedSport, locationName, searchQuery, selectedGrassType, selectedAmenities, sortBy]);

  const [currentPage, setCurrentPage] = useState(1);

  // Automatically reset to page 1 whenever filter parameters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSport, locationName, searchQuery, startDate, endDate, startTime, endTime, selectedGrassType, selectedAmenities, venueSetting, venueLevel, venueCondition, soundSystem, airConditioning, cafeteria, venueSize, minPrice, maxPrice, sortBy]);

  const handleResetFilters = () => {
    setLocationName("");
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setSelectedGrassType("any");
    setSelectedAmenities([]);
    setVenueSetting("");
    setVenueLevel("");
    setVenueCondition("");
    setSoundSystem("");
    setAirConditioning("");
    setCafeteria("");
    setVenueSize("");
    setMinPrice("");
    setMaxPrice("");
    setCurrentPage(1);
  };

  const venuesPerPage = 12; // 12 cards per page
  const indexOfLastVenue = currentPage * venuesPerPage;
  const indexOfFirstVenue = indexOfLastVenue - venuesPerPage;

  // Keep venue results consistent with Coach and Trainer listings: one
  // complete, paginated grid. Paid/featured status must never split the
  // category results into a separate promotional strip.
  const currentVenues = displayList.slice(indexOfFirstVenue, indexOfLastVenue);
  const totalPages = Math.max(1, Math.ceil(displayList.length / venuesPerPage));

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 250, behavior: "smooth" });
  };

  if (!isValidVenueCategorySlug(categorySelected)) {
    return <Error404 />;
  }

  return (
    <div style={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      <style>{`
        @media (max-width: 767.98px) {
          .hero-booking-section { padding-top: 42px !important; padding-bottom: 20px !important; }
          .hero-booking-section h1 { font-size: 30px !important; margin-bottom: 8px !important; }
          .hero-booking-section p { font-size: 16px !important; line-height: 1.55 !important; margin-bottom: 14px !important; }
          .hero-booking-section .hero-artwork-blend { width: 62% !important; opacity: 0.62 !important; }
          .hero-booking-section .rounded-pill { padding: 8px 14px !important; font-size: 12px !important; }
          .venue-category-breadcrumb { max-width: 100%; flex-wrap: wrap !important; row-gap: 4px; border-radius: 14px !important; }
          .venue-category-breadcrumb > span { margin-left: 7px !important; margin-right: 7px !important; }
          .content.blog-grid { padding-top: 18px !important; }
          .ki-mobile-listing-heading { margin-bottom: 16px !important; gap: 10px !important; align-items: flex-end !important; }
          .ki-mobile-listing-heading h2 { font-size: 22px !important; line-height: 1.15 !important; }
          .ki-mobile-listing-heading p { font-size: 12px !important; }
          .ki-mobile-sort { min-width: 142px !important; width: 142px; flex: 0 0 142px; }
          .ki-mobile-sort > span { display: none; }
          .ki-mobile-filter-toolbar { margin-bottom: 16px; }
          .ki-mobile-search .form-control { height: 40px !important; border-radius: 11px !important; }
          .ki-mobile-filter-row { display: flex; gap: 8px; overflow-x: auto; padding: 2px 1px 4px; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
          .ki-mobile-filter-row::-webkit-scrollbar { display: none; }
          .ki-mobile-filter-control { flex: 0 0 136px; min-width: 136px; }
          .ki-mobile-filter-control > .position-relative { width: 100% !important; }
          .ki-mobile-filter-control .btn { height: 38px !important; border-radius: 10px !important; }
          .ki-mobile-filter-reset { height: 38px; flex: 0 0 auto; white-space: nowrap; border-radius: 10px !important; padding: 0 12px !important; }
          .ki-featured-row { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
      `}</style>
      
      {/* Standard Hero Header Section matching other pages */}
      <div className="hero-booking-section standard-page-hero" style={{ background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)", paddingTop: "120px", paddingBottom: "42px", position: "relative", overflow: "hidden", borderBottom: "1px solid #E5E7EB" }}>
        <div className="hero-artwork-blend" style={{ position: "absolute", right: "-60px", top: 0, bottom: 0, width: "55%", backgroundImage: "url('/assets/img/bg/banner-illustration.png')", backgroundSize: "cover", backgroundPosition: "left center", backgroundRepeat: "no-repeat", maskImage: "linear-gradient(to left, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)", WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)", opacity: 0.9 }}></div>
        
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <div className="row align-items-center">
            <div className="col-lg-7 text-start">
              <span className="font-weight-bold" style={{ fontSize: "13px", letterSpacing: "1.5px", display: "block", marginBottom: "8px", color: "#22C55E", fontWeight: "700" }}>BOOK. PLAY. ENJOY</span>
              <h1 className="d-flex align-items-center flex-wrap" style={{ fontSize: "44px", fontWeight: "800", color: "#0F172A", lineHeight: "1.1", marginBottom: "12px" }}>
                {categoryTitle} <span style={{ color: "#22C55E", marginLeft: "10px" }}>Venues</span>
              </h1>
              <p style={{ color: "#64748B", fontSize: "18px", marginBottom: "16px", fontWeight: "500", maxWidth: "480px" }}>
                Browse and book top-rated {categoryListingLabel} across Indore
              </p>
              
              {/* Breadcrumb pill matching sports-venue.tsx */}
              <div className="venue-category-breadcrumb d-inline-flex align-items-center bg-white px-3 py-2 rounded-pill shadow-sm" style={{ fontSize: "13px", border: "1px solid #E5E7EB" }}>
                <Link to="/" style={{ color: "#64748B", textDecoration: "none", fontWeight: "500" }}><i className="feather-home me-1" style={{ color: "#64748B" }} /> Home</Link>
                <span style={{ margin: "0 10px", color: "#64748B" }}><i className="feather-chevron-right" style={{ fontSize: "12px", color: "#64748B" }} /></span>
                <Link to="/sports-venue" style={{ color: "#64748B", textDecoration: "none", fontWeight: "500" }}>Sports Venues</Link>
                <span style={{ margin: "0 10px", color: "#64748B" }}><i className="feather-chevron-right" style={{ fontSize: "12px", color: "#64748B" }} /></span>
                <span style={{ color: "#22C55E", fontWeight: "600" }}>{categoryTitle}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Hero Header Section */}

      {/* Main Page Container - Left Sidebar Layout */}
      <div className="content blog-grid" style={{ backgroundColor: "#F8FAFC", padding: "28px 0 100px 0", minHeight: "75vh" }}>
        <div className="container-fluid px-lg-5 px-md-4 px-3">

          {/* Title & Count Row */}
          <div className="ki-mobile-listing-heading d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4 position-relative">
            <div>
              <h2 className="fw-extrabold text-dark mb-1 d-flex align-items-center gap-2" style={{ fontSize: "24px", fontWeight: "800", color: "#17222D" }}>
                <span style={{ color: "#22C55E" }}>{displayList.length}</span> {categoryTitle} Venues
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: "13px", fontWeight: "500", color: "#64748B" }}>
                Book the best {categoryListingLabel} in Indore
              </p>
            </div>

            {/* Sort Dropdown - top right */}
            <div className="ki-mobile-sort d-flex align-items-center gap-2" style={{ minWidth: "200px" }}>
              <span className="text-muted flex-shrink-0" style={{ fontSize: "12px", fontWeight: "600" }}>Sort by:</span>
              <CustomDropdown
                options={SORT_OPTIONS}
                value={sortBy}
                onChange={setSortBy}
                placeholder="Sort by"
                icon="feather-sliders"
              />
            </div>
          </div>

          {/* Two-Column Layout: Left Filters | Right Cards */}
          <div className="row g-4 align-items-start">

            {/* LEFT COLUMN - Sticky Filter Sidebar */}
            <div className="col-lg-3 col-md-4 d-none d-md-block">
              <div className="bg-white rounded-4 p-4 shadow-sm border" style={{ borderColor: "#E2E8E3", position: "sticky", top: "100px", marginBottom: "30px" }}>
                {/* Filter Header */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="fw-bold mb-0" style={{ fontSize: "16px", color: "#0F172A", fontFamily: "Space Grotesk, sans-serif" }}>
                    <i className="feather-filter text-success me-2" />Filters
                  </h5>
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0"
                    style={{ fontSize: "11px", fontWeight: "600", color: "#22C55E" }}
                    onClick={handleResetFilters}
                  >
                    Reset All
                  </button>
                </div>

                <hr style={{ borderColor: "#F1F5F9", margin: "0 0 16px 0" }} />

                {/* Scrollable filter content */}
                <div style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto", overflowX: "clip", paddingRight: "2px" }}>

                {/* Filter 0: Search By Name */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-dark d-flex align-items-center gap-1 mb-2" style={{ fontSize: "12px", color: "#1E293B", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    <i className="feather-search text-success" style={{ fontSize: "13px" }} /> Search Venue
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      className="form-control"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name..."
                      style={{ fontSize: "13px", paddingLeft: "35px", borderRadius: "10px", height: "38px", border: "1px solid #D1D5DB" }}
                    />
                    <i className="feather-search" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#64748B", fontSize: "14px" }} />
                  </div>
                </div>

                {/* Filter 1: Location */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-dark d-flex align-items-center gap-1 mb-2" style={{ fontSize: "12px", color: "#1E293B", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    <i className="feather-map-pin text-success" style={{ fontSize: "13px" }} /> Location
                  </label>
                  <CustomDropdown
                    options={locationOptions}
                    value={locationName}
                    onChange={setLocationName}
                    placeholder="Select Location"
                    icon="feather-map-pin"
                  />
                </div>

                {/* Filter 2: Date Range */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-dark d-flex align-items-center gap-1 mb-2" style={{ fontSize: "12px", color: "#1E293B", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    <i className="feather-calendar text-success" style={{ fontSize: "13px" }} /> Date range
                  </label>
                  <div className="d-grid gap-2">
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "600", color: "#475569" }}>From</label>
                      <input
                        type="date"
                        value={startDate}
                        min={todayStr}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value && value < todayStr) {
                            setStartDate(todayStr);
                          } else {
                            setStartDate(value);
                          }
                          if (endDate && value && endDate < value) setEndDate("");
                        }}
                        className="form-control"
                        style={{ height: "42px", borderRadius: "10px", borderColor: "#E2E8F0", fontSize: "12px" }}
                      />
                    </div>
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "600", color: "#475569" }}>To</label>
                      <input
                        type="date"
                        value={endDate}
                        min={startDate || todayStr}
                        onChange={(event) => {
                          const value = event.target.value;
                          const effectiveMin = startDate || todayStr;
                          if (value && value < effectiveMin) {
                            setEndDate(effectiveMin);
                          } else {
                            setEndDate(value);
                          }
                        }}
                        className="form-control"
                        style={{ height: "42px", borderRadius: "10px", borderColor: "#E2E8F0", fontSize: "12px" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Filter 3: Time Range */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-dark d-flex align-items-center gap-1 mb-2" style={{ fontSize: "12px", color: "#1E293B", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    <i className="feather-clock text-success" style={{ fontSize: "13px" }} /> Time range
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "600", color: "#475569" }}>From</label>
                      <select
                        value={startTime}
                        onChange={(event) => handleStartTimeChange(event.target.value)}
                        className="form-select"
                        style={{ height: "42px", borderRadius: "10px", borderColor: "#E2E8F0", fontSize: "12px", color: startTime ? "#1E293B" : "#64748B" }}
                      >
                        <option value="">--:--</option>
                        {HALF_HOUR_OPTIONS.map((opt) => (
                          <option key={`start-${opt.value}`} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "600", color: "#475569" }}>To</label>
                      <select
                        value={endTime}
                        onChange={(event) => setEndTime(event.target.value)}
                        className="form-select"
                        style={{ height: "42px", borderRadius: "10px", borderColor: "#E2E8F0", fontSize: "12px", color: endTime ? "#1E293B" : "#64748B" }}
                      >
                        <option value="">--:--</option>
                        {HALF_HOUR_OPTIONS.map((opt) => {
                          const isDisabled = startTime ? opt.value <= startTime : false;
                          return (
                            <option key={`end-${opt.value}`} value={opt.value} disabled={isDisabled}>
                              {opt.label}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Filter 4: Grass Type */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-dark d-flex align-items-center gap-1 mb-2" style={{ fontSize: "12px", color: "#1E293B", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    <i className="feather-layers text-success" style={{ fontSize: "13px" }} /> Grass Type
                  </label>
                  <CustomDropdown
                    options={GRASS_OPTIONS}
                    value={selectedGrassType}
                    onChange={setSelectedGrassType}
                    placeholder="Any Grass"
                    icon="feather-layers"
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.6px" }}>Venue details</label>
                  <div className="d-grid gap-2">
                    <CustomDropdown options={VENUE_SETTING_OPTIONS} value={venueSetting} onChange={setVenueSetting} placeholder="Open / Covered" icon="feather-sun" />
                    <CustomDropdown options={VENUE_LEVEL_OPTIONS} value={venueLevel} onChange={setVenueLevel} placeholder="Ground Floor / Terrace" icon="feather-layers" />
                    <CustomDropdown options={VENUE_CONDITION_OPTIONS} value={venueCondition} onChange={setVenueCondition} placeholder="New / Old" icon="feather-award" />
                    <CustomDropdown options={YES_NO_OPTIONS} value={soundSystem} onChange={setSoundSystem} placeholder="Sound System" icon="feather-volume-2" />
                    <CustomDropdown options={AC_OPTIONS} value={airConditioning} onChange={setAirConditioning} placeholder="AC / Non-AC" icon="feather-wind" />
                    <CustomDropdown options={YES_NO_OPTIONS} value={cafeteria} onChange={setCafeteria} placeholder="Cafeteria" icon="feather-coffee" />
                    <input type="text" value={venueSize} onChange={(event) => setVenueSize(event.target.value)} className="form-control" placeholder="Size (e.g. 5v5, 100 x 50 ft)" style={{ height: "42px", borderRadius: "10px", borderColor: "#E2E8F0", fontSize: "12px" }} />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold text-dark mb-2" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.6px" }}>Price per hour</label>
                  <div className="row g-2">
                    <div className="col-6"><input type="number" min="0" value={minPrice} onChange={(event) => setMinPrice(event.target.value)} className="form-control" placeholder="Min ₹" style={{ height: "42px", borderRadius: "10px", borderColor: "#E2E8F0", fontSize: "12px" }} /></div>
                    <div className="col-6"><input type="number" min="0" value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)} className="form-control" placeholder="Max ₹" style={{ height: "42px", borderRadius: "10px", borderColor: "#E2E8F0", fontSize: "12px" }} /></div>
                  </div>
                </div>

                {/* Filter 5: Amenities */}
                <div className="mb-2">
                  <label className="form-label fw-bold text-dark d-flex align-items-center gap-1 mb-2" style={{ fontSize: "12px", color: "#1E293B", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    <i className="feather-grid text-success" style={{ fontSize: "13px" }} /> Amenities
                  </label>
                  <MultiSelectDropdown
                    options={amenityOptions}
                    selectedValues={selectedAmenities}
                    onChange={setSelectedAmenities}
                    placeholder="Any Amenities"
                    icon="feather-grid"
                    forceUpward
                  />
                </div>

                {/* Active Filters Summary */}
                {(locationName || startDate || endDate || startTime || endTime || selectedGrassType !== "any" || selectedAmenities.length > 0) && (
                  <div className="mt-4 pt-3 border-top" style={{ borderColor: "#F1F5F9" }}>
                    <p className="text-muted mb-2" style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.6px" }}>Active Filters</p>
                    <div className="d-flex flex-wrap gap-1">
                      {locationName && (
                        <span className="badge rounded-pill d-inline-flex align-items-center ki-filter-badge" style={{ backgroundColor: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0", fontSize: "11px", fontWeight: "600", padding: "4px 8px" }}>
                          <i className="feather-map-pin me-1" /> {locationName}
                          <button type="button" className="btn-close btn-close-sm ms-1" style={{ fontSize: "8px" }} onClick={() => setLocationName("")} aria-label="Remove" />
                        </span>
                      )}
                      {(startDate || endDate) && (
                        <span className="badge rounded-pill d-inline-flex align-items-center ki-filter-badge" style={{ backgroundColor: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0", fontSize: "11px", fontWeight: "600", padding: "4px 8px" }}>
                          <i className="feather-calendar me-1" /> {startDate || "Any date"} - {endDate || "Any date"}
                          <button type="button" className="btn-close btn-close-sm ms-1" style={{ fontSize: "8px" }} onClick={() => { setStartDate(""); setEndDate(""); }} aria-label="Remove" />
                        </span>
                      )}
                      {(startTime || endTime) && (
                        <span className="badge rounded-pill d-inline-flex align-items-center ki-filter-badge" style={{ backgroundColor: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0", fontSize: "11px", fontWeight: "600", padding: "4px 8px" }}>
                          <i className="feather-clock me-1" /> {startTime ? formatTime12h(startTime) : "Any time"} - {endTime ? formatTime12h(endTime) : "Any time"}
                          <button type="button" className="btn-close btn-close-sm ms-1" style={{ fontSize: "8px" }} onClick={() => { setStartTime(""); setEndTime(""); }} aria-label="Remove" />
                        </span>
                      )}
                      {selectedGrassType && selectedGrassType !== "any" && (
                        <span className="badge rounded-pill d-inline-flex align-items-center ki-filter-badge" style={{ backgroundColor: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0", fontSize: "11px", fontWeight: "600", padding: "4px 8px" }}>
                          <i className="feather-layers me-1" /> {GRASS_OPTIONS.find(o => o.value === selectedGrassType)?.label || selectedGrassType}
                          <button type="button" className="btn-close btn-close-sm ms-1" style={{ fontSize: "8px" }} onClick={() => setSelectedGrassType("any")} aria-label="Remove" />
                        </span>
                      )}
                      {selectedAmenities.map((a) => (
                        <span key={a} className="badge rounded-pill d-inline-flex align-items-center ki-filter-badge" style={{ backgroundColor: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0", fontSize: "11px", fontWeight: "600", padding: "4px 8px" }}>
                          <i className="feather-check-circle me-1" /> {a}
                          <button type="button" className="btn-close btn-close-sm ms-1" style={{ fontSize: "8px" }} onClick={() => setSelectedAmenities(selectedAmenities.filter((x) => x !== a))} aria-label="Remove" />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                </div>{/* /Scrollable filter content */}
              </div>
            </div>

            {/* RIGHT COLUMN - Venue Cards Grid */}
            <div className="col-lg-9 col-md-8">

              <div className="ki-mobile-filter-toolbar d-md-none w-100">
                <div className="ki-mobile-search position-relative mb-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name..."
                    className="form-control"
                    style={{ fontSize: "13px", paddingLeft: "35px", borderRadius: "10px", height: "38px", border: "1px solid #E2E8E3" }}
                  />
                  <i className="feather-search" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#64748B", fontSize: "14px" }} />
                </div>
                <div className="ki-mobile-filter-row" aria-label="Venue filters">
                  <div className="ki-mobile-filter-control"><CustomDropdown options={locationOptions} value={locationName} onChange={setLocationName} placeholder="Location" icon="feather-map-pin" /></div>
                  <div className="ki-mobile-filter-control">
                    <input
                      type="date"
                      value={startDate}
                      min={todayStr}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value && value < todayStr) {
                          setStartDate(todayStr);
                        } else {
                          setStartDate(value);
                        }
                        if (endDate && value && endDate < value) setEndDate("");
                      }}
                      className="form-control"
                      aria-label="From date"
                    />
                  </div>
                  <div className="ki-mobile-filter-control">
                    <input
                      type="date"
                      value={endDate}
                      min={startDate || todayStr}
                      onChange={(event) => {
                        const value = event.target.value;
                        const effectiveMin = startDate || todayStr;
                        if (value && value < effectiveMin) {
                          setEndDate(effectiveMin);
                        } else {
                          setEndDate(value);
                        }
                      }}
                      className="form-control"
                      aria-label="To date"
                    />
                  </div>
                  <div className="ki-mobile-filter-control">
                    <select
                      value={startTime}
                      onChange={(event) => handleStartTimeChange(event.target.value)}
                      className="form-select"
                      aria-label="From time"
                      style={{ fontSize: "12px", color: startTime ? "#1E293B" : "#64748B" }}
                    >
                      <option value="">From Time</option>
                      {HALF_HOUR_OPTIONS.map((opt) => (
                        <option key={`m-start-${opt.value}`} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ki-mobile-filter-control">
                    <select
                      value={endTime}
                      onChange={(event) => setEndTime(event.target.value)}
                      className="form-select"
                      aria-label="To time"
                      style={{ fontSize: "12px", color: endTime ? "#1E293B" : "#64748B" }}
                    >
                      <option value="">To Time</option>
                      {HALF_HOUR_OPTIONS.map((opt) => {
                        const isDisabled = startTime ? opt.value <= startTime : false;
                        return (
                          <option key={`m-end-${opt.value}`} value={opt.value} disabled={isDisabled}>
                            {opt.label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <button type="button" className="ki-mobile-filter-reset btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" style={{ fontSize: "12px" }} onClick={handleResetFilters}>
                    <i className="feather-refresh-cw" style={{ fontSize: "12px" }} /> Reset
                  </button>
                </div>
              </div>

              {/* Cards Grid */}
              {currentVenues.length > 0 ? (
                <div className="ki-3-col-grid">
                  {currentVenues.map((venue, index) => (
                    <div key={index} className="listing-item venue-page ki-card-hover w-100 d-flex flex-column justify-content-between" style={{ margin: 0, overflow: "hidden", backgroundColor: "#FFFFFF", borderRadius: "16px", border: "1px solid #E2E8E3", boxShadow: "0 4px 15px rgba(0,0,0,0.02)", transition: "all 0.3s ease" }}>
                      
                      {/* Card Image Cover Header */}
                      <div className="listing-img" style={{ height: "115px", position: "relative" }}>
                        <Link
                          to={`/sports-venue/${venue.vendor_type ? venue.vendor_type.replace(/\s+/g, "-").toLowerCase() : "venue"}/${venue.name.replace(/\s+/g, "-").toLowerCase()}/${venue._id}`}
                          style={{ position: "absolute", inset: 0, display: "block" }}
                        >
                          {getVenueImage(venue?.images).startsWith("http") ? (
                            <img
                              src={getVenueImage(venue?.images)}
                              className="img-fluid"
                              alt={venue.name}
                              style={{ height: "100%", width: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <ImageWithBasePath
                              src={getVenueImage(venue?.images)}
                              className="img-fluid"
                              alt={venue.name}
                              style={{ height: "100%", width: "100%", objectFit: "cover" }}
                            />
                          )}
                        </Link>
                        
                        {/* Category Badge on Top-Left */}
                        <div style={{ position: "absolute", top: "6px", left: "6px", zIndex: 2 }}>
                          <span className="badge" style={{ backgroundColor: "#22C55E", color: "#FFFFFF", fontWeight: "700", fontSize: "9px", padding: "3px 6px", borderRadius: "5px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {venue?.vendor_type ? venue.vendor_type.replace("_", " ") : "Venue"}
                          </span>
                        </div>

                        {/* Favorite Heart Button on Top-Right */}
                        <div style={{ position: "absolute", top: "6px", right: "6px", zIndex: 2 }}>
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              toggleFavorite(venue._id);
                            }}
                            className="btn btn-white rounded-circle d-flex align-items-center justify-content-center shadow-sm" 
                            style={{ width: "25px", height: "25px", padding: 0, backgroundColor: "#FFFFFF", border: "none" }}
                          >
                            <i 
                              className={favorites[venue._id] ? "fas fa-heart text-danger" : "feather-heart text-muted"} 
                              style={{ fontSize: "11px" }} 
                            />
                          </button>
                        </div>
                      </div>

                      {/* Card Content Body */}
                      <div className="listing-content news-content p-2.5" style={{ background: "#FFFFFF", padding: "12px" }}>
                        
                        {/* Rating & Standard badge */}
                        <div className="d-flex align-items-center justify-content-between mb-2" style={{ fontSize: "12px" }}>
                          <div className="rating-wrap d-flex align-items-center gap-1">
                            <i className="fas fa-star text-warning" style={{ fontSize: "11px" }} />
                            <span style={{ fontSize: "12px", fontWeight: "700", color: "#17222D" }}>{Number(venue.rating) > 0 ? Number(venue.rating).toFixed(1) : "New"}</span>
                          </div>
                          <span style={{ fontSize: "12px", color: "#606D76", fontWeight: "600" }}>
                            <span style={{ color: "#3CAB4B", fontSize: "10px", fontWeight: "800", letterSpacing: "0.3px", marginRight: "4px" }}>SIZE</span>
                            {formatVenueDimensions(getVenueSize(venue))}
                          </span>
                        </div>

                        {/* Venue Title */}
                        <h3 className="listing-title mb-1.5" style={{ fontSize: "15px", fontWeight: "700", lineHeight: "1.2" }}>
                          <Link
                            to={`/sports-venue/${venue.vendor_type ? venue.vendor_type.replace(/\s+/g, "-").toLowerCase() : "venue"}/${venue.name.replace(/\s+/g, "-").toLowerCase()}/${venue._id}`}
                            className="text-truncate d-block" style={{ color: "#17222D" }}
                            title={venue.name}
                          >
                            {venue.name}
                          </Link>
                        </h3>

                        {/* Location */}
                        <div className="d-flex align-items-center mb-2" style={{ fontSize: "12px" }}>
                          <p className="mb-0 text-truncate" style={{ fontSize: "12px", color: "#606D76" }}>
                            <i className="feather-map-pin me-1" style={{ color: "#3CAB4B" }} />
                            {venue.near_by_location || "Indore"}, Indore
                          </p>
                        </div>

                        {/* Price & Book Button */}
                        <div className="d-flex align-items-center justify-content-between pt-2" style={{ borderTop: "1px solid #F1F5F9" }}>
                          <span style={{ fontSize: "16px", fontWeight: "800", color: "#17222D" }}>
                            {venueHourlyRate(venue) !== null ? <>{"\u20B9"}{venueHourlyRate(venue)} <span style={{ fontSize: "11px", fontWeight: "normal", color: "#606D76" }}>/hr</span></> : "Contact venue"}
                          </span>
                          <Link 
                            to={`/sports-venue/${venue.vendor_type ? venue.vendor_type.replace(/\s+/g, "-").toLowerCase() : "venue"}/${venue.name.replace(/\s+/g, "-").toLowerCase()}/${venue._id}`}
                            className="btn btn-primary btn-sm rounded-pill px-3 py-1"
                            style={{ fontSize: "12px", fontWeight: "600", backgroundColor: "#22C55E", borderColor: "#22C55E" }}
                          >
                            Book Slot
                          </Link>
                        </div>

                      </div>

                    </div>
                  ))}
                </div>
              ) : (
                <div className="col-12 text-center py-5 bg-white rounded-4 border" style={{ borderColor: "#E2E8E3" }}>
                  <i className="feather-alert-circle text-muted mb-2" style={{ fontSize: "32px" }} />
                  <h5 className="fw-bold text-dark">No Venues Found</h5>
                  <p className="text-muted fs-6 mb-0">Try clearing your search filters to view available facilities</p>
                </div>
              )}

              {/* Bottom Pagination & Status Subtitle */}
              <div className="d-flex flex-column align-items-center justify-content-center mt-5 mb-4">
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap justify-content-center">
                  {/* Previous Button */}
                  <button
                    type="button"
                    className="btn d-flex align-items-center justify-content-center shadow-sm"
                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E2E8F0",
                      color: currentPage === 1 ? "#CBD5E1" : "#334155",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s ease"
                    }}
                  >
                    <i className="feather-chevron-left" />
                  </button>

                  {/* Page Number Buttons */}
                  {(() => {
                    const items: (number | string)[] = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) items.push(i);
                    } else {
                      if (currentPage <= 4) {
                        items.push(1, 2, 3, 4, 5, "...", totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        items.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                      } else {
                        items.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
                      }
                    }

                    return items.map((item, idx) => {
                      if (item === "...") {
                        return (
                          <span key={`dots-${idx}`} className="px-2 text-muted fw-bold" style={{ fontSize: "14px" }}>
                            ...
                          </span>
                        );
                      }
                      const pageNum = Number(item);
                      const isActive = pageNum === currentPage;
                      return (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => handlePageChange(pageNum)}
                          className="btn d-flex align-items-center justify-content-center shadow-sm"
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "10px",
                            backgroundColor: isActive ? "#00B050" : "#FFFFFF",
                            border: isActive ? "none" : "1px solid #E2E8F0",
                            color: isActive ? "#FFFFFF" : "#334155",
                            fontWeight: "700",
                            fontSize: "14px",
                            transition: "all 0.2s ease"
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    });
                  })()}

                  {/* Next Button */}
                  <button
                    type="button"
                    className="btn d-flex align-items-center justify-content-center shadow-sm"
                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E2E8F0",
                      color: currentPage === totalPages ? "#CBD5E1" : "#334155",
                      cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      transition: "all 0.2s ease"
                    }}
                  >
                    <i className="feather-chevron-right" />
                  </button>
                </div>

                {/* Subtitle text */}
                <p className="mb-0" style={{ fontSize: "14px", color: "#64748B", fontWeight: "500", marginTop: "8px" }}>
                  Showing <span style={{ color: "#1E293B", fontWeight: "700" }}>{displayList.length > 0 ? indexOfFirstVenue + 1 : 0}</span> to <span style={{ color: "#1E293B", fontWeight: "700" }}>{Math.min(indexOfLastVenue, displayList.length)}</span> of <span style={{ color: "#1E293B", fontWeight: "700" }}>{displayList.length}</span> venues
                </p>
              </div>

            </div>
            {/* /RIGHT COLUMN */}

          </div>
          {/* /Two-Column Layout */}

        </div>
      </div>
      {/* /Page Content */}
    </div>
  );
}
